import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { formatDate } from "@/common/utils/formatters";
import TextArea from "@/common/components/TextArea";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { rejectFiscalDocumentSchema, RejectFiscalDocumentValidationSchema } from "../../schemas/rejectFiscalDocument.schema";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import useBillingFiscalDocumentGroupRejectFiscalDocument from "../../hooks/useBillingFiscalDocumentGroupRejectFiscalDocument";

const RejectFiscalDocument = ({ groupId }: { groupId: string; }) => {
  const queryClient = useQueryClient();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { mutate: rejectFiscalDocument } = useBillingFiscalDocumentGroupRejectFiscalDocument({
    id: groupId,
    onSuccess: () => {
      setIsError(false);
      setMessages(["Nota rejeitada com sucesso"]);
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setIsError(true);
        setMessages(error.response?.data.errors.base);
      } else {
        FormTransformers.errorsTransformer({ setError })(error);
      }
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<RejectFiscalDocumentValidationSchema>({
    resolver: zodResolver(rejectFiscalDocumentSchema)
  });

  const onSubmit: SubmitHandler<RejectFiscalDocumentValidationSchema> = (data) => {
    rejectFiscalDocument(data)
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={(value) => {
          setModalIsOpen(value);
          reset();
        }}
        Trigger={(
          <Button variant="yellow">Rejeitar nota</Button>
        )}
        title="Nota fiscal"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-lg"
        cancelButtonText="Voltar para tela inicial"
        confirmButtonText="Confirmar rejeição"
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form className="flex flex-col gap-4">
            <div className="flex gap-8">
              <div className="flex flex-col gap-1">
                <p className="italic font-light text-md text-white-400">N° da nota fiscal</p>
                <p className="font-bold text-lg text-white-800">{'01234567890123456789'}</p>
              </div>
            </div>

            <div className="flex gap-8">
              <div className="flex flex-col gap-1">
                <p className="italic font-light text-md text-white-400">Data de emissão</p>
                <p className="font-bold text-lg text-white-800">{formatDate(new Date())}</p>
              </div>
            </div>

            <div>
              <TextArea
                label="Motivo da rejeição da nota fiscal"
                placeholder="Escreva aqui um motivo para cancelamento da nota fiscal"
                {...register('rejection_reason')}
                errorMessage={errors.rejection_reason?.message || undefined}
                variant={errors.rejection_reason?.message ? 'error' : 'default'}
                markAsRequired
              />
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingFiscalDocumentGroup', groupId] });
            queryClient.invalidateQueries({ queryKey: ['billingFiscalDocumentGroups'] });
          }}
        />
      )}
    </>
  );
};

export default RejectFiscalDocument;
